import { Button, Group, Modal, Select, Stack, TextInput } from "@mantine/core";
import { useCallback, useState } from "react";
import { useMachineAccounts } from "../../../../hooks/useMachineAccounts";
import { useParameterSetCollection } from "../../../../hooks/useParameterSetCollection";
import { ModalFooter } from "../../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { MachineAccountConfiguration } from "../../../../utils/backend-types";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";

interface EditMachineAccountModalProps {
  variantId: number;
  machineAccount: MachineAccountConfiguration | null;
  onClose: () => void;
}

function EditMachineAccountModal({
  variantId,
  machineAccount,
  onClose
}: EditMachineAccountModalProps) {
  const { showErrorToast } = useToast();

  const editMode = !!machineAccount && !!machineAccount.id;
  const [selectedParameterSetId, setSelectedParameterSetId] = useState<
    string | null
  >(editMode ? machineAccount.parameterSet : null);

  const { data, isLoading, error } = useParameterSetCollection();

  const {
    createMachineAccountConfiguration,
    updateMachineAccountConfiguration
  } = useMachineAccounts(variantId);

  const parameterSets = (data || []).map(({ id, name }) => ({
    label: name,
    value: id
  }));

  const onSubmit = useCallback(() => {
    if (!selectedParameterSetId) {
      return;
    }

    try {
      if (machineAccount?.id) {
        updateMachineAccountConfiguration(
          machineAccount.id,
          selectedParameterSetId
        );
      } else {
        createMachineAccountConfiguration(selectedParameterSetId);
      }
    } catch (e) {
      showErrorToast(e);
    }

    onClose();
  }, [
    createMachineAccountConfiguration,
    machineAccount?.id,
    onClose,
    selectedParameterSetId,
    updateMachineAccountConfiguration,
    showErrorToast
  ]);

  return (
    <Modal
      className="EditMachineAccountModal"
      opened
      title={
        editMode
          ? "Machine Account bearbeiten"
          : "Neuen Machine Account hinzufügen"
      }
      onClose={onClose}
    >
      <Stack>
        {editMode && (
          <TextInput
            disabled
            label="E-Mail"
            value={machineAccount?.email || ""}
          />
        )}
        <LoadOrError error={error} loading={isLoading}>
          <Select
            data={parameterSets}
            defaultValue={editMode ? machineAccount.parameterSet : undefined}
            label="Dateiformat auswählen"
            required
            onChange={(newValue) => setSelectedParameterSetId(newValue)}
          />
        </LoadOrError>
      </Stack>
      <ModalFooter>
        <Group>
          <Button variant="default" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            color="secondary"
            disabled={!selectedParameterSetId}
            onClick={onSubmit}
          >
            Speichern
          </Button>
        </Group>
      </ModalFooter>
    </Modal>
  );
}

export { EditMachineAccountModal };
